// src/lib/api.js
// Centralized Fetch wrapper that injects Clerk token

export const API_BASE = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.trim()) || "/api";

/**
 * Perform an authenticated fetch to the Worker API.
 * @param {string} endpoint - e.g. '/creations/recent'
 * @param {object} options - fetch options (method, body, etc.)
 * @param {function} getToken - from useAuth() / useClerk()
 */
export async function apiFetch(endpoint, options = {}, getToken) {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (getToken) {
    const token = await getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Allow overriding base URL for testing hangs (DEV ONLY)
  let finalBaseUrl = API_BASE;
  if (import.meta.env.DEV && typeof window !== 'undefined' && localStorage.getItem('DEBUG_HANG') === '1') {
      finalBaseUrl = 'http://10.255.255.1'; // Non-routable IP to simulate hang
      console.warn('⚠️ DEBUG_HANG active: Request will hang until timeout.');
  }

  const url = `${finalBaseUrl}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
  
  // Step 3: Enforce strict client-side timeout to prevent infinite loading.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120000); // 120s timeout

  try {
    const res = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal // Bind explicit timeout signal
    });

    if (res.status === 401) {
      // Optionally handle logout or refresh
      console.warn("API 401 Unauthorized");
    }

    // Read response as text first to enable detailed error reporting
    const text = await res.text();
    
    // Attempt to parse JSON
    let data;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (parseError) {
      // Throw detailed error with response snippet for debugging
      const snippet = text.slice(0, 200);
      throw new Error(`API_JSON_PARSE_ERROR [${res.status}] ${url} :: Response snippet: ${snippet}`);
    }

    // Check for HTTP errors
    if (!res.ok) {
        // If data.details exists, include it in the error message for easier debugging
        const errorMessage = data?.details 
            ? `${data.error}: ${data.details}` 
            : (data?.error || `API_HTTP_${res.status}`);
        throw new Error(errorMessage);
    }

    return data;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function createApi(getToken) {
  return {
    get: (url) => apiFetch(url, { method: 'GET' }, getToken),
    post: (url, body) => {
      // FAL-ONLY GUARDRAIL
      if (url.includes('/generate')) {
        if (body?.provider !== 'fal') {
           throw new Error(`GUARDRAIL: Invalid provider '${body?.provider}'. Must be 'fal'.`);
        }
        if (!['reference', 'hair-change'].includes(body?.mode)) { // standard -> hair-change
           throw new Error(`GUARDRAIL: Invalid mode '${body?.mode}'. Must be 'reference' or 'hair-change'.`);
        }
      }
      return apiFetch(url, { method: 'POST', body: JSON.stringify(body) }, getToken);
    },
    put: (url, body) => apiFetch(url, { method: 'PUT', body: JSON.stringify(body) }, getToken),
    patch: (url, body) => apiFetch(url, { method: 'PATCH', body: JSON.stringify(body) }, getToken),
    del: (url) => apiFetch(url, { method: 'DELETE' }, getToken)
  };
}
